import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  Users, Eye, AlertTriangle, TrendingUp, Clock, Activity, RefreshCw,
  ChevronLeft, ChevronRight, Monitor, Smartphone, Globe, BarChart3,
  CheckCircle, XCircle, Trash2, BookOpen, MapPin, Filter, Route,
  ArrowDown, LogIn, CreditCard, MousePointerClick
} from 'lucide-react';
import { API } from '@/config';
import { toast } from 'sonner';

const T = {
  title: { ar: 'تقارير الزوار', sv: 'Besöksanalys', en: 'Analytics' },
  overview: { ar: 'نظرة عامة', sv: 'Översikt', en: 'Overview' },
  tests: { ar: 'الاختبارات', sv: 'Prov', en: 'Tests' },
  journeys: { ar: 'رحلة المستخدم', sv: 'Användarresor', en: 'User Journeys' },
  funnel: { ar: 'تحليل القمع', sv: 'Trattanalys', en: 'Funnel' },
  visitors: { ar: 'الزوار', sv: 'Besökare', en: 'Visitors' },
  errors: { ar: 'الأخطاء', sv: 'Fel', en: 'Errors' },
  totalVisits: { ar: 'إجمالي الزيارات', sv: 'Totala besök', en: 'Total Visits' },
  uniqueVisitors: { ar: 'زوار فريدين', sv: 'Unika besökare', en: 'Unique Visitors' },
  registered: { ar: 'مسجلين', sv: 'Registrerade', en: 'Registered' },
  totalErrors: { ar: 'الأخطاء', sv: 'Fel', en: 'Errors' },
  topPages: { ar: 'الصفحات الأكثر زيارة', sv: 'Mest besökta sidor', en: 'Top Pages' },
  topActions: { ar: 'الإجراءات الشائعة', sv: 'Vanligaste åtgärder', en: 'Top Actions' },
  devices: { ar: 'الأجهزة', sv: 'Enheter', en: 'Devices' },
  browsers: { ar: 'المتصفحات', sv: 'Webbläsare', en: 'Browsers' },
  dailyVisits: { ar: 'الزيارات اليومية', sv: 'Dagliga besök', en: 'Daily Visits' },
  refresh: { ar: 'تحديث', sv: 'Uppdatera', en: 'Refresh' },
  noData: { ar: 'لا توجد بيانات', sv: 'Ingen data', en: 'No data' },
  testsStarted: { ar: 'بدأوا اختبار', sv: 'Startade prov', en: 'Started' },
  testsCompleted: { ar: 'أكملوا', sv: 'Slutförde', en: 'Completed' },
  testsAbandoned: { ar: 'غادروا', sv: 'Avbröt', en: 'Abandoned' },
  completionRate: { ar: 'معدل الإكمال', sv: 'Slutförandegrad', en: 'Completion Rate' },
  avgScore: { ar: 'متوسط النتيجة', sv: 'Genomsnitt', en: 'Avg Score' },
  testName: { ar: 'الاختبار', sv: 'Prov', en: 'Test' },
  status: { ar: 'الحالة', sv: 'Status', en: 'Status' },
  answered: { ar: 'الإجابات', sv: 'Svar', en: 'Answered' },
  score: { ar: 'النتيجة', sv: 'Poäng', en: 'Score' },
  language: { ar: 'اللغة', sv: 'Språk', en: 'Language' },
  time: { ar: 'الوقت', sv: 'Tid', en: 'Time' },
  completed: { ar: 'مكتمل', sv: 'Avslutad', en: 'Completed' },
  abandoned: { ar: 'غادر', sv: 'Avbruten', en: 'Abandoned' },
  inProgress: { ar: 'جاري', sv: 'Pågår', en: 'In Progress' },
  cleanup: { ar: 'تنظيف الفارغة', sv: 'Rensa tomma', en: 'Clean Empty' },
  cleanupConfirm: { ar: 'حذف الجلسات الفارغة والقديمة. متأكد؟', sv: 'Ta bort tomma sessioner. Säker?', en: 'Delete empty sessions. Sure?' },
  clearAll: { ar: 'مسح الكل', sv: 'Rensa allt', en: 'Clear All' },
  clearAllConfirm: { ar: 'حذف بيانات القمع والرحلات والاختبارات والزوار والأخطاء؟ النظرة العامة لن تُحذف.', sv: 'Radera tratt, resor, tester, besökare och fel? Översikten behålls.', en: 'Delete funnel, journeys, tests, visitors and errors? Overview will be kept.' },
  exitPage: { ar: 'صفحة الخروج', sv: 'Utgångssida', en: 'Exit Page' },
  duration: { ar: 'المدة', sv: 'Varaktighet', en: 'Duration' },
  actions: { ar: 'الإجراءات', sv: 'Åtgärder', en: 'Actions' },
  journey: { ar: 'الرحلة', sv: 'Resa', en: 'Journey' },
  guest: { ar: 'زائر', sv: 'Gäst', en: 'Guest' },
  visit: { ar: 'زيارة الموقع', sv: 'Besök webbplats', en: 'Visit Site' },
  view_tests: { ar: 'عرض الاختبارات', sv: 'Visa prov', en: 'View Tests' },
  start_test: { ar: 'بدء اختبار', sv: 'Starta prov', en: 'Start Test' },
  complete_test: { ar: 'إكمال اختبار', sv: 'Slutför prov', en: 'Complete Test' },
  view_pricing: { ar: 'عرض الأسعار', sv: 'Visa priser', en: 'View Pricing' },
  click_subscribe: { ar: 'نقر اشتراك', sv: 'Klicka prenumerera', en: 'Click Subscribe' },
  login: { ar: 'تسجيل دخول', sv: 'Logga in', en: 'Login' },
  dropoffPages: { ar: 'صفحات الخروج الشائعة', sv: 'Vanliga utgångssidor', en: 'Common Exit Pages' },
  bounceRate: { ar: 'معدل الارتداد', sv: 'Avvisningsfrekvens', en: 'Bounce Rate' },
  conversionFunnel: { ar: 'قمع التحويل', sv: 'Konverteringstratt', en: 'Conversion Funnel' },
  errorType: { ar: 'النوع', sv: 'Typ', en: 'Type' },
  errorMsg: { ar: 'الرسالة', sv: 'Meddelande', en: 'Message' },
  page: { ar: 'الصفحة', sv: 'Sida', en: 'Page' },
  device: { ar: 'الجهاز', sv: 'Enhet', en: 'Device' },
  pagesViewed: { ar: 'الصفحات', sv: 'Sidor', en: 'Pages' },
  exitReason: { ar: 'سبب الخروج', sv: 'Avslutningsorsak', en: 'Exit Reason' },
  reason_bounce: { ar: 'لم يتفاعل (ارتداد)', sv: 'Studsar (ingen interaktion)', en: 'Bounced (no interaction)' },
  reason_paywall: { ar: 'رأى جدار الاشتراك وغادر', sv: 'Såg betalvägg och lämnade', en: 'Saw paywall and left' },
  reason_error: { ar: 'واجه خطأ تقني', sv: 'Stötte på tekniskt fel', en: 'Encountered error' },
  reason_test_abandoned: { ar: 'بدأ اختبار ولم يكمله', sv: 'Startade prov men slutförde inte', en: 'Started test but abandoned' },
  reason_pricing_hesitation: { ar: 'شاهد الأسعار ولم يشترك', sv: 'Såg priser men prenumererade inte', en: 'Viewed pricing but did not subscribe' },
  reason_completed: { ar: 'أكمل المهمة', sv: 'Slutförde uppgiften', en: 'Completed task' },
  reason_natural: { ar: 'غادر بشكل طبيعي', sv: 'Lämnade naturligt', en: 'Left naturally' },
  reason_unknown: { ar: 'غير محدد', sv: 'Okänt', en: 'Unknown' },
  timeOnPage: { ar: 'الوقت في الصفحة', sv: 'Tid på sidan', en: 'Time on page' },
};

const timeAgo = (dateStr, lang) => {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  const isAr = lang === 'ar' || lang === 'fa';
  if (m < 1) return isAr ? 'الآن' : 'now';
  if (m < 60) return isAr ? `${m} د` : `${m}m`;
  if (h < 24) return isAr ? `${h} س` : `${h}h`;
  return isAr ? `${d} ي` : `${d}d`;
};

const formatDuration = (sec, lang) => {
  if (!sec || sec <= 0) return '-';
  const isAr = lang === 'ar' || lang === 'fa';
  if (sec < 60) return isAr ? `${sec} ثانية` : `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return isAr ? `${m} دقيقة` : `${m}m`;
  const h = Math.floor(m / 60);
  return isAr ? `${h}س ${m % 60}د` : `${h}h ${m % 60}m`;
};

const actionIcons = {
  page_view: Eye,
  page_exit: ArrowDown,
  session_end: XCircle,
  test_start: BookOpen,
  test_complete: CheckCircle,
  test_abandoned: XCircle,
  test_question_view: BookOpen,
  subscribe_click: CreditCard,
  pricing_view: CreditCard,
  paywall_shown: CreditCard,
  paywall_dismissed: CheckCircle,
  login: LogIn,
  google_login: LogIn,
  logout: XCircle,
  language_change: Globe,
  lesson_view: BookOpen,
  sign_view: MapPin,
  speed_challenge: Activity,
  error: AlertTriangle,
};

const actionColors = {
  page_view: 'bg-blue-100 text-blue-600',
  page_exit: 'bg-slate-100 text-slate-500',
  session_end: 'bg-slate-200 text-slate-600',
  test_start: 'bg-emerald-100 text-emerald-600',
  test_complete: 'bg-green-100 text-green-700',
  test_abandoned: 'bg-red-100 text-red-600',
  subscribe_click: 'bg-purple-100 text-purple-600',
  pricing_view: 'bg-purple-100 text-purple-600',
  paywall_shown: 'bg-red-100 text-red-600',
  paywall_dismissed: 'bg-green-100 text-green-600',
  login: 'bg-indigo-100 text-indigo-600',
  google_login: 'bg-indigo-100 text-indigo-600',
  language_change: 'bg-amber-100 text-amber-600',
  error: 'bg-red-100 text-red-600',
};

const exitReasonConfig = {
  bounce: { color: 'bg-slate-100 text-slate-600', icon: XCircle },
  paywall: { color: 'bg-red-100 text-red-600', icon: CreditCard },
  error: { color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  test_abandoned: { color: 'bg-amber-100 text-amber-700', icon: BookOpen },
  pricing_hesitation: { color: 'bg-purple-100 text-purple-600', icon: CreditCard },
  completed: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  natural: { color: 'bg-blue-100 text-blue-600', icon: ArrowDown },
  unknown: { color: 'bg-slate-100 text-slate-500', icon: Activity },
};

const MiniBar = ({ data }) => {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-[2px] h-16">
      {data.map((d, i) => (
        <div key={i} className="flex-1 group relative">
          <div className="bg-emerald-400 rounded-t transition-all hover:bg-emerald-500 min-h-[2px]" style={{ height: `${Math.max((d.count / max) * 100, 3)}%` }} />
          <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-10">{d.date?.slice(5)}: {d.count}</div>
        </div>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <Card className="p-4 flex items-start gap-3">
    <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
    <div>
      <p className="text-2xl font-bold text-slate-800">{value ?? 0}</p>
      <p className="text-xs text-slate-500">{label}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </Card>
);

const ProgressBar = ({ label, count, max, color = 'bg-emerald-500' }) => (
  <div className="flex items-center gap-2 text-sm">
    <span className="text-slate-600 truncate w-32 text-xs">{label}</span>
    <div className="flex-1 bg-slate-100 rounded-full h-2">
      <div className={`h-2 rounded-full ${color}`} style={{ width: `${Math.min((count / Math.max(max, 1)) * 100, 100)}%` }} />
    </div>
    <span className="text-slate-500 text-xs font-medium w-10 text-end">{count}</span>
  </div>
);

const Pagination = ({ current, total: totalPages, onChange }) => {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 py-3">
      <Button variant="outline" size="sm" onClick={() => onChange(Math.max(1, current - 1))} disabled={current <= 1}><ChevronLeft className="w-4 h-4" /></Button>
      <span className="text-xs text-slate-500">{current} / {totalPages}</span>
      <Button variant="outline" size="sm" onClick={() => onChange(Math.min(totalPages, current + 1))} disabled={current >= totalPages}><ChevronRight className="w-4 h-4" /></Button>
    </div>
  );
};

const AnalyticsDashboard = ({ token }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const t = useCallback((key) => T[key]?.[lang] || T[key]?.en || key, [lang]);
  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [errors, setErrors] = useState(null);
  const [testSessions, setTestSessions] = useState(null);
  const [journeys, setJourneys] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [expandedJourney, setExpandedJourney] = useState(null);
  const [visitorsPage, setVisitorsPage] = useState(1);
  const [errorsPage, setErrorsPage] = useState(1);
  const [testPage, setTestPage] = useState(1);
  const [journeyPage, setJourneyPage] = useState(1);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, testRes, funnelRes] = await Promise.all([
        fetch(`${API}/admin/analytics/summary?days=${period}`, { headers }),
        fetch(`${API}/admin/analytics/test-sessions?days=${period}&page=1&limit=20`, { headers }),
        fetch(`${API}/admin/analytics/funnel?days=${period}`, { headers }),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (testRes.ok) setTestSessions(await testRes.json());
      if (funnelRes.ok) setFunnel(await funnelRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [period, headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchTab = useCallback(async (t, p) => {
    try {
      const urls = {
        visitors: `${API}/admin/analytics/visitors?days=${period}&page=${p}&limit=20`,
        errors: `${API}/admin/analytics/errors?days=${period}&page=${p}&limit=20`,
        tests: `${API}/admin/analytics/test-sessions?days=${period}&page=${p}&limit=20`,
        journeys: `${API}/admin/analytics/journeys?days=${period}&page=${p}&limit=15`,
      };
      if (!urls[t]) return;
      const res = await fetch(urls[t], { headers });
      if (!res.ok) return;
      const data = await res.json();
      if (t === 'visitors') setVisitors(data);
      if (t === 'errors') setErrors(data);
      if (t === 'tests') setTestSessions(data);
      if (t === 'journeys') setJourneys(data);
    } catch (e) { console.error(e); }
  }, [period, headers]);

  useEffect(() => {
    if (tab === 'visitors') fetchTab('visitors', visitorsPage);
    if (tab === 'errors') fetchTab('errors', errorsPage);
    if (tab === 'tests') fetchTab('tests', testPage);
    if (tab === 'journeys') fetchTab('journeys', journeyPage);
  }, [tab, visitorsPage, errorsPage, testPage, journeyPage, fetchTab]);

  const cleanup = async () => {
    if (!window.confirm(t('cleanupConfirm'))) return;
    try {
      const res = await fetch(`${API}/admin/analytics/cleanup-test-sessions`, { method: 'DELETE', headers });
      if (res.ok) { const d = await res.json(); toast.success(`${d.deleted?.total || 0} deleted`); fetchAll(); }
    } catch (e) { console.error(e); }
  };

  const clearAll = async () => {
    if (!window.confirm(t('clearAllConfirm'))) return;
    try {
      const res = await fetch(`${API}/admin/analytics/clear-all`, { method: 'DELETE', headers });
      if (res.ok) { 
        const d = await res.json(); 
        toast.success(`${d.deleted?.total || 0} deleted`); 
        setSummary(null); setVisitors(null); setErrors(null); setTestSessions(null); setJourneys(null); setFunnel(null);
        fetchAll(); 
      }
    } catch (e) { console.error(e); }
  };

  const tabs = [
    { id: 'overview', label: t('overview'), icon: BarChart3 },
    { id: 'funnel', label: t('funnel'), icon: Filter },
    { id: 'journeys', label: t('journeys'), icon: Route },
    { id: 'tests', label: t('tests'), icon: BookOpen },
    { id: 'visitors', label: t('visitors'), icon: Users },
    { id: 'errors', label: t('errors'), icon: AlertTriangle },
  ];

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-slate-800">{t('title')}</h2>
        <div className="flex items-center gap-2">
          {[7, 14, 30, 90].map(d => (
            <button key={d} onClick={() => setPeriod(d)} className={`px-3 py-1 rounded-full text-xs font-medium transition ${period === d ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {d}{lang === 'ar' ? ' يوم' : 'd'}
            </button>
          ))}
          <Button variant="outline" size="sm" onClick={fetchAll} className="gap-1"><RefreshCw className="w-3.5 h-3.5" /></Button>
          <Button variant="destructive" size="sm" onClick={clearAll} className="gap-1" data-testid="clear-all-analytics-btn"><Trash2 className="w-3.5 h-3.5" />{t('clearAll')}</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg overflow-x-auto">
        {tabs.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)} className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition flex-1 justify-center whitespace-nowrap ${tab === tb.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <tb.icon className="w-3.5 h-3.5" />
            {tb.label}
            {tb.id === 'errors' && summary?.total_errors > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{summary.total_errors}</span>}
          </button>
        ))}
      </div>

      {/* ===== Overview Tab ===== */}
      {tab === 'overview' && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Eye} label={t('totalVisits')} value={summary.total_visits} color="bg-blue-50 text-blue-600" />
            <StatCard icon={Users} label={t('uniqueVisitors')} value={summary.unique_visitors} color="bg-emerald-50 text-emerald-600" />
            <StatCard icon={TrendingUp} label={t('registered')} value={summary.registered_users} color="bg-purple-50 text-purple-600" />
            <StatCard icon={AlertTriangle} label={t('totalErrors')} value={summary.total_errors} color="bg-red-50 text-red-600" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Card className="p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('dailyVisits')}</h3>
              {summary.daily_visits?.length > 0 ? (<><MiniBar data={summary.daily_visits} /><div className="flex justify-between mt-1 text-[10px] text-slate-400"><span>{summary.daily_visits[0]?.date?.slice(5)}</span><span>{summary.daily_visits[summary.daily_visits.length - 1]?.date?.slice(5)}</span></div></>) : <p className="text-sm text-slate-400 text-center py-6">{t('noData')}</p>}
            </Card>
            {testSessions?.stats && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('tests')}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between"><div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-blue-500" /><span className="text-xs text-slate-600">{t('testsStarted')}</span></div><span className="font-bold text-slate-800">{testSessions.stats.total_started}</span></div>
                  <div className="flex justify-between"><div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-xs text-slate-600">{t('testsCompleted')}</span></div><span className="font-bold text-emerald-600">{testSessions.stats.total_completed}</span></div>
                  <div className="flex justify-between"><div className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /><span className="text-xs text-slate-600">{t('testsAbandoned')}</span></div><span className="font-bold text-red-600">{testSessions.stats.total_abandoned}</span></div>
                  <div className="border-t pt-2 mt-2 space-y-1">
                    <div className="flex justify-between text-xs"><span className="text-slate-500">{t('completionRate')}</span><span className="font-bold text-amber-600">{testSessions.stats.completion_rate}%</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">{t('avgScore')}</span><span className="font-bold text-purple-600">{testSessions.stats.avg_score}%</span></div>
                  </div>
                </div>
              </Card>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('topPages')}</h3>
              <div className="space-y-2">
                {summary.top_pages?.slice(0, 6).map((p, i) => <ProgressBar key={i} label={p.page || '/'} count={p.count} max={summary.top_pages[0]?.count} color="bg-blue-400" />)}
                {!summary.top_pages?.length && <p className="text-xs text-slate-400 text-center py-4">{t('noData')}</p>}
              </div>
            </Card>
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('topActions')}</h3>
              <div className="space-y-2">
                {summary.top_actions?.slice(0, 6).map((a, i) => <ProgressBar key={i} label={a.action || '-'} count={a.count} max={summary.top_actions[0]?.count} color="bg-emerald-400" />)}
                {!summary.top_actions?.length && <p className="text-xs text-slate-400 text-center py-4">{t('noData')}</p>}
              </div>
            </Card>
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('devices')}</h3>
              <div className="space-y-2">{summary.device_stats?.map((d, i) => <ProgressBar key={i} label={d.device} count={d.count} max={summary.device_stats[0]?.count} color="bg-purple-400" />)}</div>
              {summary.browser_stats?.length > 0 && (<><h3 className="text-sm font-semibold text-slate-700 mt-4 mb-3">{t('browsers')}</h3><div className="space-y-2">{summary.browser_stats?.slice(0, 4).map((b, i) => <ProgressBar key={i} label={b.browser} count={b.count} max={summary.browser_stats[0]?.count} color="bg-amber-400" />)}</div></>)}
            </Card>
          </div>
        </div>
      )}

      {/* ===== Funnel Tab ===== */}
      {tab === 'funnel' && funnel && (
        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">{t('conversionFunnel')}</h3>
            <div className="space-y-2">
              {funnel.funnel?.map((step, i) => {
                const prev = i > 0 ? funnel.funnel[i - 1].count : step.count;
                const dropoff = prev > 0 ? Math.round((1 - step.count / prev) * 100) : 0;
                const icons = { visit: Eye, view_tests: BookOpen, start_test: MousePointerClick, complete_test: CheckCircle, view_pricing: CreditCard, click_subscribe: CreditCard, login: LogIn };
                const Icon = icons[step.step] || Eye;
                const colors = ['bg-blue-500', 'bg-cyan-500', 'bg-emerald-500', 'bg-green-500', 'bg-purple-500', 'bg-violet-500', 'bg-indigo-500'];
                return (
                  <div key={step.step}>
                    <div className="flex items-center gap-3">
                      <div className={`p-1.5 rounded-lg ${colors[i] || 'bg-slate-500'} bg-opacity-10`}><Icon className={`w-4 h-4 ${colors[i]?.replace('bg-', 'text-') || 'text-slate-500'}`} /></div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium text-slate-700">{t(step.step)}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-800">{step.count}</span>
                            <span className="text-[10px] text-slate-400">({step.rate}%)</span>
                          </div>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3">
                          <div className={`h-3 rounded-full ${colors[i] || 'bg-slate-400'} transition-all`} style={{ width: `${step.rate}%` }} />
                        </div>
                      </div>
                    </div>
                    {i > 0 && dropoff > 0 && i < funnel.funnel.length && (
                      <div className="flex items-center gap-1 mr-9 my-1">
                        <ArrowDown className="w-3 h-3 text-red-400" />
                        <span className="text-[10px] text-red-400">-{dropoff}% {lang === 'ar' ? 'خسارة' : 'drop'}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('dropoffPages')}</h3>
              <div className="space-y-2">
                {funnel.dropoffs?.map((d, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-red-400" />
                      <span className="text-xs text-slate-600 font-mono">{d.page}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-slate-400">{d.avg_actions} {lang === 'ar' ? 'إجراء' : 'actions'}</span>
                      <span className="text-xs font-bold text-red-500">{d.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-700">{t('bounceRate')}</h3>
                <span className={`text-2xl font-bold ${funnel.bounce_rate > 50 ? 'text-red-500' : funnel.bounce_rate > 30 ? 'text-amber-500' : 'text-emerald-500'}`}>{funnel.bounce_rate}%</span>
              </div>
              <p className="text-xs text-slate-400">{lang === 'ar' ? `${funnel.total_visitors} زائر - الزوار الذين شاهدوا صفحة أو صفحتين فقط ثم غادروا` : `${funnel.total_visitors} visitors - who viewed 1-2 pages then left`}</p>
            </Card>
          </div>
        </div>
      )}

      {/* ===== Journeys Tab ===== */}
      {tab === 'journeys' && (
        <div className="space-y-3">
          {journeys?.journeys?.length > 0 ? (
            <>
              {journeys.journeys.map((j, i) => (
                <Card key={i} className={`overflow-hidden transition cursor-pointer ${expandedJourney === i ? 'ring-2 ring-emerald-300' : 'hover:shadow-md'}`} onClick={() => setExpandedJourney(expandedJourney === i ? null : i)}>
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${j.email ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {j.email ? j.email[0].toUpperCase() : (j.device === 'mobile' ? <Smartphone className="w-4 h-4" /> : <Monitor className="w-4 h-4" />)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">{j.email || `${t('guest')} #${j.visitor_id?.slice(0, 6)}`}</p>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400">
                          <span>{j.device}</span>
                          <span>|</span>
                          <span>{j.language?.toUpperCase()}</span>
                          <span>|</span>
                          <span>{j.total_actions} {t('actions')}</span>
                          <span>|</span>
                          <span>{formatDuration(j.duration_sec, lang)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {j.entered_test && <BookOpen className={`w-4 h-4 ${j.completed_test ? 'text-emerald-500' : 'text-amber-400'}`} />}
                      {j.clicked_subscribe && <CreditCard className="w-4 h-4 text-purple-500" />}
                      {j.saw_paywall && <CreditCard className="w-4 h-4 text-red-400" />}
                      <div className="text-end">
                        {j.exit_reason && j.exit_reason !== 'unknown' && (() => {
                          const cfg = exitReasonConfig[j.exit_reason] || exitReasonConfig.unknown;
                          const ReasonIcon = cfg.icon;
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.color} mb-0.5`}>
                              <ReasonIcon className="w-3 h-3" />
                              {t(`reason_${j.exit_reason}`)}
                            </span>
                          );
                        })()}
                        <p className="text-[10px] text-slate-400">{timeAgo(j.last_action, lang)}</p>
                        <p className="text-[10px] text-red-400 font-mono truncate max-w-[120px]">{lang === 'ar' ? 'خرج:' : 'exit:'} {j.exit_page?.split('/').pop() || '/'}</p>
                      </div>
                    </div>
                  </div>
                  {expandedJourney === i && (
                    <div className="border-t bg-slate-50 p-3">
                      {/* Page times summary */}
                      {j.page_times && Object.keys(j.page_times).length > 0 && (
                        <div className="mb-3 p-2 bg-white rounded border border-slate-100">
                          <p className="text-[10px] font-semibold text-slate-500 mb-1">{t('timeOnPage')}</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(j.page_times).map(([pg, sec], pi) => (
                              <span key={pi} className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                                {pg.split('/').pop() || '/'}: {formatDuration(sec, lang)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {j.actions?.map((a, ai) => {
                          const Icon = actionIcons[a.action] || Activity;
                          const color = actionColors[a.action] || 'bg-slate-100 text-slate-600';
                          const isImportant = ['paywall_shown', 'test_start', 'test_complete', 'subscribe_click', 'error', 'test_abandoned'].includes(a.action);
                          return (
                            <div key={ai} className={`flex items-center gap-2 py-1 ${isImportant ? 'bg-amber-50 rounded px-1' : ''}`}>
                              <div className="w-5 text-center text-[10px] text-slate-300">{ai + 1}</div>
                              <div className={`p-1 rounded ${color}`}><Icon className="w-3 h-3" /></div>
                              <span className={`text-xs font-medium ${isImportant ? 'text-amber-700' : 'text-slate-600'}`}>{a.action}</span>
                              <span className="text-[10px] text-slate-400 font-mono truncate flex-1">{a.page}</span>
                              {a.details && <span className="text-[10px] text-slate-400 truncate max-w-[100px]">{a.details}</span>}
                              <span className="text-[10px] text-slate-300">{timeAgo(a.timestamp, lang)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
              <Pagination current={journeyPage} total={journeys.pages} onChange={setJourneyPage} />
            </>
          ) : (
            <Card className="p-10 text-center text-slate-400"><Route className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>{t('noData')}</p></Card>
          )}
        </div>
      )}

      {/* ===== Tests Tab ===== */}
      {tab === 'tests' && (
        <div className="space-y-3">
          <div className="flex justify-end"><Button variant="destructive" size="sm" onClick={cleanup} className="gap-1.5" data-testid="cleanup-test-btn"><Trash2 className="w-3.5 h-3.5" />{t('cleanup')}</Button></div>
          {testSessions?.stats && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <StatCard icon={BookOpen} label={t('testsStarted')} value={testSessions.stats.total_started} color="bg-blue-50 text-blue-600" />
              <StatCard icon={CheckCircle} label={t('testsCompleted')} value={testSessions.stats.total_completed} color="bg-emerald-50 text-emerald-600" />
              <StatCard icon={XCircle} label={t('testsAbandoned')} value={testSessions.stats.total_abandoned} color="bg-red-50 text-red-600" />
              <StatCard icon={TrendingUp} label={t('completionRate')} value={`${testSessions.stats.completion_rate}%`} color="bg-amber-50 text-amber-600" />
              <StatCard icon={Activity} label={t('avgScore')} value={`${testSessions.stats.avg_score}%`} color="bg-purple-50 text-purple-600" />
            </div>
          )}
          {testSessions?.sessions?.length > 0 ? (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b"><tr>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('testName')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('status')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('answered')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('score')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('language')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('time')}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {testSessions.sessions.map((s, i) => (
                      <tr key={i} className={`hover:bg-slate-50 transition ${s.status === 'abandoned' && s.answers_count === 0 ? 'opacity-40' : ''}`}>
                        <td className="px-4 py-2.5 font-medium text-slate-700">{s.test_name || s.test_id}</td>
                        <td className="px-4 py-2.5"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${s.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : s.status === 'abandoned' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{t(s.status === 'in_progress' ? 'inProgress' : s.status)}</span></td>
                        <td className="px-4 py-2.5 text-slate-600">{s.answers_count}/{s.total_questions}</td>
                        <td className="px-4 py-2.5">{s.score_percentage != null ? <span className={`font-semibold ${s.score_percentage >= 70 ? 'text-emerald-600' : 'text-red-500'}`}>{s.score_percentage}%</span> : <span className="text-slate-300">-</span>}</td>
                        <td className="px-4 py-2.5 uppercase text-xs text-slate-500">{s.language || '-'}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-400" title={s.started_at ? new Date(s.started_at).toLocaleString() : ''}>{timeAgo(s.started_at, lang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination current={testPage} total={testSessions.pages} onChange={setTestPage} />
            </Card>
          ) : <Card className="p-10 text-center text-slate-400"><Activity className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>{t('noData')}</p></Card>}
        </div>
      )}

      {/* ===== Visitors Tab ===== */}
      {tab === 'visitors' && (
        <div className="space-y-3">
          {visitors?.visitors?.length > 0 ? (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b"><tr>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">ID</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('totalVisits')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('pagesViewed')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('duration')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('device')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('status')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('time')}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {visitors.visitors.map((v, i) => (
                      <tr key={i} className="hover:bg-slate-50 transition">
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{v.visitor_id?.slice(0, 12)}...</td>
                        <td className="px-4 py-2.5 font-semibold text-slate-700">{v.total_visits}</td>
                        <td className="px-4 py-2.5 text-slate-600">{v.pages_count}</td>
                        <td className="px-4 py-2.5 text-xs font-medium text-blue-600">{formatDuration(v.duration_sec, lang)}</td>
                        <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1 text-xs text-slate-500">{v.device_type === 'mobile' ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}{v.device_type || '-'}</span></td>
                        <td className="px-4 py-2.5"><span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${v.is_registered ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{v.is_registered ? t('registered') : t('guest')}</span></td>
                        <td className="px-4 py-2.5 text-xs text-slate-400">{timeAgo(v.last_visit, lang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination current={visitorsPage} total={visitors.pages} onChange={setVisitorsPage} />
            </Card>
          ) : <Card className="p-10 text-center text-slate-400"><Users className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>{t('noData')}</p></Card>}
        </div>
      )}

      {/* ===== Errors Tab ===== */}
      {tab === 'errors' && (
        <div className="space-y-3">
          {errors?.error_types?.length > 0 && <div className="flex flex-wrap gap-2">{errors.error_types.map((et, i) => <span key={i} className="px-2.5 py-1 bg-red-50 text-red-600 rounded-full text-xs font-medium">{et.type}: {et.count}</span>)}</div>}
          {errors?.errors?.length > 0 ? (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b"><tr>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('errorType')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('errorMsg')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('page')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('device')}</th>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold text-slate-500 uppercase">{t('time')}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {errors.errors.map((e, i) => (
                      <tr key={i} className="hover:bg-slate-50 transition">
                        <td className="px-4 py-2.5"><span className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-[11px] font-medium">{e.error_type}</span></td>
                        <td className="px-4 py-2.5 max-w-xs"><p className="text-slate-600 truncate text-xs" title={e.error_message}>{e.error_message?.slice(0, 80)}</p></td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{e.page}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-400">{e.device_type}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-400">{timeAgo(e.created_at, lang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination current={errorsPage} total={errors.pages} onChange={setErrorsPage} />
            </Card>
          ) : <Card className="p-10 text-center text-slate-400"><AlertTriangle className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>{t('noData')}</p></Card>}
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
